import { useCallback } from "react";

import { FormStatus, DonationFormCategory, isAddressMandatoryToGenerateReceipt } from "@simplyk/common";

import { useCurrentUserContext } from "../../../../common/contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../common/contexts/LocaleContext";

import { useIsOnboarding } from "@/common/hooks/useIsOnboarding";
import { useTranslate } from "@/common/hooks/useTranslate";
import { CreateDonationFormInput } from "@/common/types/trpc";
import { getDefaultFormColor } from "@/features/LiveFormEditor/common/defaultFormColorByCategory";
import { getDonationFormEmailTemplates } from "@/features/LiveFormEditor/common/getDonationFormEmailTemplates";

export const useCreateDonationFormInput = () => {
  const { currentUser, organization } = useCurrentUserContext();
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isOnboarding } = useIsOnboarding();
  const canGenerateReceipt = Boolean(organization?.canGenerateReceipt);

  return useCallback(
    (formCategory: DonationFormCategory): CreateDonationFormInput => {
      const getPrefilledTitle = (formCategory: DonationFormCategory) => {
        switch (formCategory) {
          case DonationFormCategory.DonationForm:
            return t("dashboard", "liveFormEditor.stepTitle.donationForm.placeholder");
          case DonationFormCategory.PeerToPeerCampaign:
            return t("dashboard", "liveFormEditor.stepTitle.p2p.placeholder");
          default:
            return t("dashboard", "liveFormEditor.stepTitle.donationForm.placeholder");
        }
      };

      const emailTemplates = getDonationFormEmailTemplates(formCategory, t, currentUser);

      const sanitizedEmail = currentUser?.email ? currentUser.email : null;

      const fundraiserEmailBodyPlaceholder = t("dashboard", "donationForm.defaultFundraiserEmailBody", {
        firstName: currentUser?.firstName || "",
        lastName: currentUser?.lastName || "",
        email: sanitizedEmail || "",
      });

      const fundraiserEmailBodyPlaceholderParam =
        formCategory === DonationFormCategory.PeerToPeerCampaign
          ? {
              fundraiserEmailBody: fundraiserEmailBodyPlaceholder,
            }
          : {};

      const isP2P = formCategory === DonationFormCategory.PeerToPeerCampaign;

      return {
        hasReceipt: canGenerateReceipt,
        color: organization?.color || getDefaultFormColor(formCategory),
        locale,
        category: formCategory,
        target: isP2P ? 10000 : undefined,
        allowFundraiserCreation: isP2P,
        allowJoinTeam: isP2P,
        allowTeamCreation: isP2P,
        askForAddress: canGenerateReceipt && isAddressMandatoryToGenerateReceipt(organization?.country),
        donationFormFields: [
          {
            title: getPrefilledTitle(formCategory),
            description: "",
            chequeDescription: "",
            donationFormAmounts: [],
            locale,
            emailObject: emailTemplates.emailObject,
            emailContent: emailTemplates.emailContent,
            ...fundraiserEmailBodyPlaceholderParam,
          },
        ],
        questions: [],
        questionsToDelete: [],
        duplicateLogo: true,
        notificationEmails: sanitizedEmail ? [sanitizedEmail] : null,
        isOnboardingForm: isOnboarding,
        status: FormStatus.Draft,
        banner: null,
        liveFormEditorStep: "title",
      };
    },
    [canGenerateReceipt, currentUser, isOnboarding, locale, organization?.color, organization?.country, t]
  );
};
